import * as React from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  ContentLayout,
  HeaderBreadcrumbs,
  useDropdownApis,
  Loader,
} from "../../../shared";
import InputControl from "../../../shared/components/InputControl";
import { useParams, useLocation } from "react-router-dom";
import { useAddOrEditPointPromotion } from "../hooks";
import moment from "moment";
import SimpleReactValidator from "simple-react-validator";
import { AsyncPaginate } from "react-select-async-paginate";

const pointPromotionTypes = [
  { label: "Sales Multiplier", value: "sales_multiplier" },
  { label: "Sales Bonus", value: "sales_bonus" },
];

const dayOptions = [
  { label: "SUN", value: 0 },
  { label: "MON", value: 1 },
  { label: "TUE", value: 2 },
  { label: "WED", value: 3 },
  { label: "THU", value: 4 },
  { label: "FRI", value: 5 },
  { label: "SAT", value: 6 },
];

export const AddorEditPointPromotion = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const [, forceUpdate] = React.useState();
  const validator = React.useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } }),
  );

  const {
    state,
    globalData,
    handleChange,
    handleLoyaltyCardsList,
    onSavePointPromotion,
  } = useAddOrEditPointPromotion({ id });
  const { handleSegmentList } = useDropdownApis({});

  const cardTypeOptions = globalData?.points_promotions?.card_type;

  const hasSelectedDays =
    (state.data.days || []).filter((d) => !Number.isNaN(Number(d))).length > 0;
  const hasTimeRange = Boolean(state.data.start_time && state.data.end_time);
  const isDayOrTimeValid =
    !state.data.limit_by_days_time || hasSelectedDays || hasTimeRange;

  const getTimePickerValue = (timeValue) => {
    if (!timeValue) return null;

    const parsedTime = moment(
      timeValue,
      ["HH:mm:ss", "HH:mm", "hh:mm A"],
      true,
    );

    return parsedTime.isValid() ? parsedTime.toDate() : null;
  };

  if (state.isLoading) {
    return <Loader />;
  }

  const handleSubmit = () => {
    if (validator.current.allValid() && isDayOrTimeValid) {
      onSavePointPromotion("draft");
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };

  return (
    <>
      {validator.current.purgeFields()}
      <ContentLayout
        title={id ? "Edit Promotion" : "New Promotion"}
        rightContent={
          <Button
            type="button"
            size="medium"
            variant="contained"
            disabled={state.isButtonBusy}
            onClick={handleSubmit}
          >
            {id ? "Update" : "Save as Draft"}
          </Button>
        }
        breadcrumbs={
          <HeaderBreadcrumbs
            links={[
              {
                name: "Dashboard",
                href: isStoreAdmin ? "/store-admin" : "/",
              },
              {
                name: "Point Promotions",
                href: isStoreAdmin
                  ? "/store-admin/point-promotions"
                  : "/point-promotions",
              },
              ...(id
                ? [
                    {
                      name: state?.data?.name || "Promotion Details",
                      href: isStoreAdmin
                        ? `/store-admin/point-promotions/details/${id}`
                        : `/point-promotions/details/${id}`,
                    },
                  ]
                : []),
              { name: id ? "Edit Promotion" : "Add Promotion" },
            ]}
          />
        }
      >
        <Stack gap="16px">
          <Card sx={{ p: "20px", borderRadius: "8px" }}>
            <Typography variant="subtitle1" fontWeight={600} mb="14px">
              Promotion Type
            </Typography>
            <ToggleButtonGroup
              value={state.data.type}
              exclusive
              onChange={(_, value) => {
                if (value) {
                  handleChange({
                    target: {
                      name: "type",
                      value,
                    },
                  });
                }
              }}
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
                overflow: "hidden",
                "& .MuiToggleButton-root": {
                  border: "none",
                  color: "text.secondary",
                  textTransform: "none",
                  px: 2,
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "primary.main",
                    },
                  },
                },
              }}
            >
              {pointPromotionTypes.map((type) => (
                <ToggleButton key={type.value} value={type.value}>
                  {type.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Card>

          <Card sx={{ p: "20px", borderRadius: "8px" }}>
            <Typography variant="subtitle1" fontWeight={600} mb="16px">
              Details
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <InputControl
                  name="name"
                  label="Promotion Name"
                  value={state.data.name}
                  onChange={handleChange}
                  required
                  error={validator.current.message(
                    "Promotion Name",
                    state.data.name,
                    "required",
                  )}
                  helperText={validator.current.message(
                    "Promotion Name",
                    state.data.name,
                    "required",
                  )}
                />

                <InputControl
                  type="select"
                  label="Card Type"
                  name="card_type"
                  value={state.data.card_type}
                  optionValue={cardTypeOptions ?? []}
                  onChange={handleChange}
                  required
                  error={validator.current.message(
                    "Card Type",
                    state.data.card_type,
                    "required",
                  )}
                  helperText={validator.current.message(
                    "Card Type",
                    state.data.card_type,
                    "required",
                  )}
                />
              </Stack>

              {state.data.card_type === "customer" && (
                <Box>
                  <AsyncPaginate
                    isMulti
                    controlShouldRenderValue={false}
                    hideSelectedOptions={true}
                    debounceTimeout={1000}
                    className="AsyncPaginate-dropdown"
                    classNamePrefix="react-select"
                    placeholder="Customer Segment"
                    menuPosition="fixed"
                    menuPlacement="bottom"
                    menuPortalTarget={
                      typeof window !== "undefined" ? document.body : null
                    }
                    value={state.data.segment_ids || []}
                    loadOptions={async (e, p, { page }) => {
                      return await handleSegmentList({
                        search: e,
                        page,
                      });
                    }}
                    onChange={(selectedOptions) => {
                      handleChange({
                        target: {
                          name: "segment_ids",
                          value: selectedOptions || [],
                        },
                      });
                    }}
                    additional={{
                      page: 1,
                    }}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  <Stack
                    sx={{
                      flexDirection: "row",
                      gap: "4px",
                      alignItems: "center",
                      padding: "6px 4px",
                    }}
                  >
                    <img
                      style={{
                        height: "12px",
                      }}
                      alt="details"
                      src="/icons/ic_info.svg"
                    />
                    <Typography
                      variant="caption"
                      lineHeight="15px"
                      color="#666666"
                    >
                      All customers are applied by default. Edit if needed.
                    </Typography>
                  </Stack>
                  {state.data.segment_ids?.length > 0 && (
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      gap={1}
                      sx={{ mt: 1 }}
                    >
                      {state.data.segment_ids.map((segment) => (
                        <Chip
                          key={segment.value}
                          label={segment.label}
                          size="small"
                          onDelete={() => {
                            handleChange({
                              target: {
                                name: "segment_ids",
                                value: (state.data.segment_ids || []).filter(
                                  (item) => item.value !== segment.value,
                                ),
                              },
                            });
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              {state.data.card_type === "sponsorship" && (
                <Box>
                  <AsyncPaginate
                    isMulti
                    controlShouldRenderValue={false}
                    hideSelectedOptions={true}
                    debounceTimeout={1000}
                    className="AsyncPaginate-dropdown"
                    classNamePrefix="react-select"
                    placeholder="Group"
                    menuPosition="fixed"
                    menuPlacement="bottom"
                    menuPortalTarget={
                      typeof window !== "undefined" ? document.body : null
                    }
                    value={state.data.loyalty_card_group_ids || []}
                    loadOptions={async (e, p, { page }) => {
                      return await handleLoyaltyCardsList({
                        search: e,
                        page,
                      });
                    }}
                    onChange={(selectedOptions) => {
                      handleChange({
                        target: {
                          name: "loyalty_card_group_ids",
                          value: selectedOptions || [],
                        },
                      });
                    }}
                    additional={{
                      page: 1,
                    }}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {state.data.loyalty_card_group_ids?.length > 0 && (
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      gap={1}
                      sx={{ mt: 1 }}
                    >
                      {state.data.loyalty_card_group_ids.map((card) => (
                        <Chip
                          key={card.value}
                          label={card.label}
                          size="small"
                          onDelete={() => {
                            handleChange({
                              target: {
                                name: "loyalty_card_group_ids",
                                value: (
                                  state.data.loyalty_card_group_ids || []
                                ).filter((item) => item.value !== card.value),
                              },
                            });
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  <Stack
                    sx={{
                      flexDirection: "row",
                      gap: "4px",
                      alignItems: "center",
                      padding: "6px 4px",
                    }}
                  >
                    <img
                      style={{
                        height: "18px",
                      }}
                      alt="details"
                      src="/icons/ic_info.svg"
                    />
                    <Typography
                      variant="caption"
                      lineHeight="15px"
                      color="#666666"
                    >
                      All groups are applied by default. Edit if needed.
                    </Typography>
                  </Stack>
                </Box>
              )}

              {state.data.type === "sales_multiplier" && (
                <InputControl
                  name="multiplier_value"
                  label="Multiplier Value"
                  type="number"
                  value={state.data.multiplier_value}
                  onChange={handleChange}
                  required
                  width={"50%"}
                  error={validator.current.message(
                    "Multiplier Value",
                    state.data.multiplier_value,
                    "required|numeric",
                  )}
                  helperText={validator.current.message(
                    "Multiplier Value",
                    state.data.multiplier_value,
                    "required|numeric",
                  )}
                />
              )}

              {state.data.type === "sales_bonus" && (
                <InputControl
                  name="bonus_value"
                  label="Bonus Value"
                  type="number"
                  value={state.data.bonus_value || ""}
                  onChange={handleChange}
                  required
                  width={"50%"}
                  error={validator.current.message(
                    "Bonus Value",
                    state.data.bonus_value,
                    "required|numeric",
                  )}
                  helperText={validator.current.message(
                    "Bonus Value",
                    state.data.bonus_value,
                    "required|numeric",
                  )}
                />
              )}
            </Stack>
          </Card>

          <Card sx={{ p: "20px", borderRadius: "8px" }}>
            <Typography variant="subtitle1" fontWeight={600} mb="16px">
              Schedule
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <InputControl
                  type="datePicker"
                  name="start_date"
                  label="Start Date"
                  inputFormat="dd-MM-yyyy"
                  minDate={new Date()}
                  sx={{ width: "50%" }}
                  value={state.data.start_date || ""}
                  onKeyDown={(e) => {
                    e.preventDefault();
                  }}
                  onChange={(e) => {
                    handleChange({
                      target: {
                        name: "start_date",
                        value: e ? moment(e).format("YYYY-MM-DD") : "",
                      },
                    });
                  }}
                  required
                  error={validator.current.message(
                    "Start Date",
                    state.data.start_date,
                    "required",
                  )}
                  helperText={validator.current.message(
                    "Start Date",
                    state.data.start_date,
                    "required",
                  )}
                />

                <InputControl
                  type="datePicker"
                  name="end_date"
                  label="End Date"
                  inputFormat="dd-MM-yyyy"
                  sx={{ width: "50%" }}
                  value={state.data.end_date || ""}
                  onKeyDown={(e) => {
                    e.preventDefault();
                  }}
                  minDate={state.data.start_date || new Date()}
                  onChange={(e) => {
                    handleChange({
                      target: {
                        name: "end_date",
                        value: e ? moment(e).format("YYYY-MM-DD") : "",
                      },
                    });
                  }}
                  required
                  error={validator.current.message(
                    "End Date",
                    state.data.end_date,
                    "required",
                  )}
                  helperText={validator.current.message(
                    "End Date",
                    state.data.end_date,
                    "required",
                  )}
                />
              </Stack>

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="limit_by_days_time"
                      checked={state.data.limit_by_days_time}
                      onChange={handleChange}
                    />
                  }
                  label="Limit by specific days & Time"
                />
                <Typography variant="body2" color="text.secondary" ml="32px">
                  By default, it applies to all days & times
                </Typography>

                {state.data.limit_by_days_time && (
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                    mt={3}
                    ml="32px"
                  >
                    <Stack gap="1px">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        mb="12px"
                        color="#666666"
                      >
                        Day(s)
                      </Typography>

                      <Stack
                        direction="row"
                        gap="8px"
                        mb="16px"
                        flexWrap="wrap"
                      >
                        {dayOptions.map((day, index) => {
                          const normalizedDays = (state.data.days || [])
                            .map((d) => Number(d))
                            .filter((d) => !Number.isNaN(d));
                          const isDaySelected = normalizedDays.includes(
                            day.value,
                          );

                          return (
                            <ToggleButton
                              key={day.value}
                              value={day.value}
                              selected={isDaySelected}
                              onChange={() => {
                                const updatedDays = isDaySelected
                                  ? normalizedDays.filter((d) => d !== day.value)
                                  : [...normalizedDays, day.value];

                                handleChange({
                                  target: {
                                    name: "days",
                                    value: updatedDays,
                                  },
                                });
                              }}
                              size="small"
                              sx={{
                                px: 1.5,
                                textTransform: "none",
                                border: "1px solid #e0e0e0",
                                "&:hover": {
                                  backgroundColor: "primary.main",
                                  color: "#fff",
                                  borderColor: "primary.main",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: "primary.main",
                                  color: "#fff",
                                  border: "1px solid",
                                  borderColor: "primary.main",
                                  "&:hover": {
                                    backgroundColor: "primary.main",
                                    borderColor: "primary.main",
                                  },
                                },
                              }}
                            >
                              {day.label}
                            </ToggleButton>
                          );
                        })}
                      </Stack>
                    </Stack>
                    <Box
                      sx={{
                        width: "2px",
                        height: "40px",
                        backgroundColor: "#e0e0e0",
                        mx: 2,
                      }}
                    />
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      width="50%"
                      spacing={2}
                    >
                      <InputControl
                        ampm={false}
                        inputFormat="HH:mm"
                        type="timePicker"
                        name="start_time"
                        label="Start Time"
                        value={
                          getTimePickerValue(state.data.start_time)
                        }
                        onChange={(time) => {
                          handleChange({
                            target: {
                              name: "start_time",
                              value: time
                                ? moment(time).format("HH:mm:ss")
                                : null,
                            },
                          });
                        }}
                        style={{ width: "50%" }}
                        error={
                          !hasSelectedDays &&
                          state.data.limit_by_days_time &&
                          validator.current.message(
                            "Start Time",
                            state.data.start_time,
                            "required",
                          )
                        }
                        helperText={
                          !hasSelectedDays &&
                          state.data.limit_by_days_time &&
                          validator.current.message(
                            "Start Time",
                            state.data.start_time,
                            "required",
                          )
                        }
                      />

                      <InputControl
                        ampm={false}
                        inputFormat="HH:mm"
                        type="timePicker"
                        name="end_time"
                        label="End Time"
                        value={
                          getTimePickerValue(state.data.end_time)
                        }
                        onChange={(time) => {
                          handleChange({
                            target: {
                              name: "end_time",
                              value: time
                                ? moment(time).format("HH:mm:ss")
                                : null,
                            },
                          });
                        }}
                        style={{ width: "50%" }}
                        error={
                          !hasSelectedDays &&
                          state.data.limit_by_days_time &&
                          validator.current.message(
                            "End Time",
                            state.data.end_time,
                            "required",
                          )
                        }
                        helperText={
                          !hasSelectedDays &&
                          state.data.limit_by_days_time &&
                          validator.current.message(
                            "End Time",
                            state.data.end_time,
                            "required",
                          )
                        }
                      />
                    </Stack>
                  </Stack>
                )}
                {state.data.limit_by_days_time && !isDayOrTimeValid && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ ml: "32px", mt: 1, display: "block" }}
                  >
                    Please select at least one day or set both start and end
                    time.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Card>
        </Stack>
      </ContentLayout>
    </>
  );
};
